/* eslint-disable @next/next/no-img-element */
import {
  AuthFill,
  Button,
  CompanyName,
  CompanyType,
  DealerFinder,
  Email,
  FirstName,
  LastName,
  MarketingConsent,
  ModelGrid,
  Postcode,
  Tel,
  Title,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav, StepIndicator } from '@components/ui'
import { useCampaign, useDealer, useDealerContext } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import {
  closeOverlay,
  getModelImage,
  useFormSteps,
} from '@utils/helpers/index-client'
import { getContentFile, getModels, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { GetAQuoteSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

type FormData = {
  modelImage?: string
  modelName?: string
  fuel?: string[]
  label?: string
  dealerID?: string
  dealerName?: string
  dealerDistance?: string
  address?:
    | string
    | {
        address?: string
        address1?: string
        city?: string
        zip?: string
      }
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
}

type SelectedDealer = {
  id?: number
  name?: string
  distance?: number
  address?: {
    address?: string
    address1?: string
    city?: string
    zip?: string
  }
}

const GetAQuote = ({
  models,
  content,
  title,
  tracking,
  brand,
  dealer,
  model,
  query,
}) => {
  const campaign = useCampaign()
  const {
    formStep,
    user,
    expandedStep,
    goToStep,
    toggleExpand,
    closeAllExpands,
  } = useFormSteps(1)
  const [formData, setFormData] = useState<FormData>({})
  const [submitResponse, setSubmitResponse] = useState('')
  const [selectedDealer, setSelectedDealer] = useState<SelectedDealer>({})
  const clientDealer = useDealer()
  const dealerContext = useDealerContext()

  if (clientDealer) {
    // eslint-disable-next-line no-param-reassign
    dealer = clientDealer
  }
  const findLocalDealerLink =
    brand === 'lexus'
      ? '<a class="underline" target="_blank" rel="noopener noreferrer" href="https://www.lexus.co.uk/forms/find-a-centre">find your local Centre</a>'
      : '<a class="underline" target="_blank" rel="noopener noreferrer" href="https://www.toyota.co.uk/find-a-dealer">find your local dealer</a>'

  // Alias for backward compatibility
  const step = goToStep
  const configID = (query?.configId || query?.code)?.replace(
    /[^a-zA-Z0-9]/g,
    '',
  )

  const getNotesText = () => {
    if (query?.pch === 'true') {
      return 'This customer has requested a PCH quote'
    }
    if (query?.bch === 'true') {
      return 'This customer  is interested in BCH offers'
    }
    return ''
  }

  const getType = () => {
    if (query?.bch === 'true') {
      return 'BQ'
    }
    if (query?.pch === 'true') {
      return 'CH'
    }
    return 'CP'
  }
  const initialValues = {
    type: getType(),
    name: 'get-a-quote',
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    tel: '',
    model: '',
    date: '',
    notes: getNotesText(),
    consent: {
      user: false,
    },
    response_code: '',
    company_name: '',
    company_type: '',
    postcode: '',
    campaign_response_code: '',
    campaign_code: '',
    crm_response_source: '',
    configID: configID ? `tme.one/${configID}` : '',
    is_dealer: query?.source === 'dealer' || query?.source === 'centre',
    dealer: 0,
    dealer_name: '',
    is_lexus: brand === 'lexus',
    pvt_company_car: query?.bch === 'true' ? 'B' : '',
  }

  const methods = useForm({
    resolver: yupResolver(GetAQuoteSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const handleSetDealer = (dealerObj) => {
    methods.setValue('dealer', dealerObj.id)
    methods.setValue('dealer_name', dealerObj.name)
    step(3, true)
    setSelectedDealer(dealerObj)
  }

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }
  }, [campaign])

  useEffect(() => {
    if (model) {
      if (brand === 'lexus') {
        methods.setValue('response_code', model.responsecodes.get_a_quote)
      }
      methods.setValue('model', model.d4t4.id)
      setFormData((state) => {
        return {
          ...state,
          modelImage: getModelImage(
            brand,
            model?.image?.id,
            model?.image?.grade,
            model?.image?.color,
            'webp',
            model?.image?.fallback,
          ),
          modelName: model.name,
          fuel: model?.filters?.fuel,
        }
      })
      step(dealer ? 3 : 2, false)
    }
  }, [model])

  useEffect(() => {
    if (dealer) {
      methods.setValue('is_dealer', true)
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )

      setSelectedDealer(dealer)
      // Use preferredDealerName from postMessage if available, otherwise use API result
      const dealerName = dealerContext?.preferredDealerName || dealer.name
      methods.setValue('dealer_name', dealerName)
      setFormData((state) => {
        return {
          ...state,
          dealerID: dealer.applications.NMSC.externalID,
          dealerName,
          dealerDistance: null,

          address: {
            address: dealer.addresses[0].addressLine1,
            address1: dealer.addresses[0].addressLine2,
            city: dealer.addresses[0].city,
            zip: dealer.addresses[0].zip,
          },
        }
      })

      step(model ? 3 : 1, false)
    }
  }, [dealer, dealerContext?.preferredDealerName])

  // Step change handlers
  const handleVehicleChange = () => {
    step(1, true)
    closeAllExpands()
    methods.setValue('dealer', 0)
    methods.setValue('dealer_name', '')
    setSelectedDealer({})
    setFormData((state) => ({
      ...state,
      modelImage: '',
      modelName: '',
      fuel: undefined,
      dealerID: '',
      dealerName: '',
      dealerDistance: '',
      address: '',
    }))
    setSubmitResponse('')
  }

  const handleDealerChange = () => {
    step(2, true)
    closeAllExpands()
    methods.setValue('dealer', 0)
    methods.setValue('dealer_name', '')
    setSelectedDealer({})
  }

  const handleDetailsChange = () => {
    step(4, true)
    closeAllExpands()
    setFormData((state) => ({
      ...state,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    }))
  }

  // Step indicator components using StepIndicator
  const VehicleStep = () => (
    <StepIndicator
      stepNumber={1}
      label={brand === 'lexus' ? 'Car' : 'Vehicle'}
      currentStep={formStep}
      isCompleted={!!formData.modelName}
      isExpanded={expandedStep === 'vehicle'}
      summaryText={
        formData.modelName
          ? `${formData.modelName}${formData.label ? ` ${formData.label}` : ''}`
          : undefined
      }
      onChangeClick={handleVehicleChange}
      onExpandToggle={() => toggleExpand('vehicle')}
      trackingName="get-a-quote"
      expandedContent={
        formData.modelName ? (
          <>
            <div className="text-lg font-medium">{formData.modelName}</div>
            {formData.fuel !== undefined &&
              formData.fuel.some(
                (f) =>
                  f.toLowerCase() !== 'petrol' &&
                  f.toLowerCase() !== 'diesel',
              ) && (
                <div
                  className={`${
                    brand === 'toyota' ? 'font-medium text-blue' : ''
                  } uppercase`}
                >
                  {formData.fuel.join(' & ')}
                </div>
              )}
            <img
              className="w-full font-medium mt-4"
              src={formData.modelImage}
              alt={formData.modelName}
            />
          </>
        ) : undefined
      }
    />
  )

  const DealerStep = () => (
    <StepIndicator
      stepNumber={2}
      label={brand === 'lexus' ? 'Centre' : 'Dealer'}
      currentStep={formStep}
      isCompleted={!!selectedDealer.name}
      isExpanded={expandedStep === 'dealer'}
      summaryText={selectedDealer?.name}
      onChangeClick={handleDealerChange}
      onExpandToggle={() => toggleExpand('dealer')}
      trackingName="get-a-quote"
      expandedContent={
        selectedDealer?.name ? (
          <>
            <div className="text-tiny lowercase">
              {selectedDealer.distance &&
                `${(selectedDealer.distance * 0.62137119).toFixed(2)} miles`}
            </div>
            <div className="my-[10px] font-medium">{selectedDealer?.name}</div>
            {selectedDealer?.address?.address && (
              <div className="normal-case">
                {selectedDealer?.address?.address}
              </div>
            )}
            {selectedDealer?.address?.address1 && (
              <div className="normal-case">
                {selectedDealer?.address?.address1}
              </div>
            )}
            {selectedDealer?.address?.city && (
              <div className="normal-case">{selectedDealer?.address?.city}</div>
            )}
            {selectedDealer?.address?.zip && (
              <div className="normal-case">{selectedDealer?.address?.zip}</div>
            )}
          </>
        ) : undefined
      }
    />
  )

  const DetailsStep = () => (
    <StepIndicator
      stepNumber={3}
      label="Details"
      currentStep={formStep}
      isCompleted={!!formData.first_name}
      isExpanded={expandedStep === 'details'}
      summaryText={formData.first_name}
      onChangeClick={handleDetailsChange}
      onExpandToggle={() => toggleExpand('details')}
      trackingName="get-a-quote"
      expandedContent={
        formData.first_name ? (
          <>
            <div>{`${formData.first_name} ${formData.last_name}`}</div>
            <div>{formData.email}</div>
            <div>{formData.phone}</div>
          </>
        ) : undefined
      }
    />
  )

  const onSubmit = async (data) => {
    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/get-a-quote`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: tracking.variant,
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: tracking.variant,
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
        steps={[VehicleStep(), DealerStep(), DetailsStep()]}
      />
      <FormWrapper>
        <FormStep
          title={content.car.title}
          step={formStep}
          active={formStep === 1}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'select-model',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <ModelGrid
            models={models}
            showFilter={content.car.filter}
            setModel={(selectedModel) => {
              methods.setValue(
                'response_code',
                selectedModel?.responsecodes?.get_a_quote || '',
              )

              methods.setValue('model', selectedModel.d4t4.id)
              setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }, 100)
              if (methods.getValues('dealer')) {
                step(3, true)
              } else {
                step(2, true)
              }

              setFormData((state) => {
                return {
                  ...state,
                  modelImage: getModelImage(
                    brand,
                    selectedModel?.image?.id,
                    selectedModel?.image?.grade,
                    selectedModel?.image?.color,
                    'webp',
                    selectedModel?.image?.fallback,
                  ),
                  modelName: selectedModel.name,
                  fuel: selectedModel?.filters?.fuel || undefined,
                }
              })
            }}
          />
        </FormStep>
      </FormWrapper>

      <FormStep
        step={formStep}
        active={formStep === 2}
        user={user}
        noContainer={true}
        tracking={{
          conversion: 0,
          name: tracking.form,
          step: 'select-dealer-get-location',
          variant: tracking.variant,
          page: tracking.page,
        }}
      >
        <div className="relative z-[1]">
          <DealerFinder setDealer={handleSetDealer} />
        </div>
      </FormStep>

      <FormWrapper>
        <FormStep
          title={content.details.title}
          step={formStep}
          active={formStep === 3}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'contact-details',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <p className="mb-8">{content.details.subtitle}</p>
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Title />
              <FirstName />
              <LastName />
              {query?.bch === 'true' ? (
                <>
                  <CompanyName
                    helper={`If you are a private customer, please ${findLocalDealerLink} who can help.`}
                  />
                  <CompanyType
                    label="How many vehicles are you interested in?"
                    radiolabels={['1-5 vehicles', '6+ vehicles']}
                  />
                </>
              ) : null}

              <Email />
              <Tel />
              {query?.bch === 'true' ? <Postcode /> : null}
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'get-a-quote'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  const getQuoteConfig = () => {
    if (context.query?.bch === 'true') {
      return {
        variant: 'get-a-quote-bch',
        title: 'Business Contract Hire Quote',
        subtitle: content.attributes.details.subtitle_bch,
      }
    }
    if (context.query?.pch === 'true') {
      return {
        variant: 'get-a-quote-pch',
        title: 'Personal Contract Hire Quote',
        subtitle: content.attributes.details.subtitle_pch,
      }
    }
    return {
      variant: 'get-a-quote',
      title: 'Personal Contract Purchase Quote',
      subtitle: content.attributes.details.subtitle_pcp,
    }
  }

  const config = getQuoteConfig()

  return {
    props: {
      models: getModels(brand, form),
      content: {
        ...content.attributes,
        details: {
          ...content.attributes.details,
          subtitle: config.subtitle,
        },
      },
      title: config.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: config.variant,
        initialStep: 'select-model',
      },
      ...(await getPageData(context, form)),
    },
  }
}

export default GetAQuote
